import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { HeaderAction, MAT_CORE_CONFIG, MatCoreConfig, TrackingContext } from "@obg/mat.ui.core";
import { CommonState, GAMING_GAME_LAUNCHERS } from "@obg/ngx.common";
import {
	CORE_ROUTE,
	CoreUiInteractionEvent,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	UiInteraction,
	WalletSelector,
} from "@obg/ngx.core";
import { CoreContentState, MenuItemGroup, MenuSelector, TranslationService } from "@obg/ngx.core.content";
import { coerceBoolean, TelemetryService } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { BetaBadgeService } from "@obg/ngx.ui.core";
import { ignoreNil } from "@obg/rxjs";
import { upperFirst } from "lodash-es";
import { combineLatest, merge, Subscription } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { DropdownMenuComponent } from "../dropdown-menu/dropdown-menu.component";
import { MyAccountEvent } from "../telemetry/ui-interaction-telemetry.model";

import { FundsOverviewOptions } from "./funds-overview/funds-overview.model";
import { UserSummaryConfigService } from "./user-summary.config";

const BALANCE_INTERACTION: UiInteraction = {
	event: CoreUiInteractionEvent.Navigate,
	context: TrackingContext.MyAccountInteractionContext,
};

@Component({
	selector: "obg-m-user-summary-container",
	templateUrl: "./user-summary.container.html",
	styleUrls: ["./user-summary.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class UserSummaryContainer implements OnInit, OnDestroy {
	@ViewChild("menu", { static: true }) menu: DropdownMenuComponent | undefined;

	@HostBinding("class.obg-m-user-summary") hostCssClass = true;

	@Input() @coerceBoolean() showRunningBalanceMsg: boolean | undefined;

	opened = false;

	readonly menuItems$ = combineLatest([
		this.store.select(MenuSelector.get("header-account-menu")).pipe(ignoreNil()),
		...(!this.matCoreConfig?.betaLinkDisabled ? [this.betaBadgeService.showBetaLinkAlternative$()] : []),
	]).pipe(
		map(([menu, showBetaLink]: [MenuItemGroup, boolean]) =>
			showBetaLink
				? [
						...menu.items.slice(0, menu.items.length - 1),
						{
							key: this.BETA_BADGE_KEY,
							title: upperFirst(this.translationService.getByKey(this.BETA_BADGE_KEY)),
							iconTag: "ico-arrow-exit",
						},
						...menu.items.slice(menu.items.length - 1),
					]
				: menu.items
		)
	);

	readonly customer$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(ignoreNil());

	readonly balance$ = this.store.pipe(
		select(JurisdictionSelector.get),
		ignoreNil(),
		switchMap((jurisdiction) =>
			this.store.pipe(
				select(
					jurisdiction === Jurisdiction.Aams
						? this.walletSelector.getBalance()
						: this.walletSelector.getCurrentProductBalance()
				)
			)
		)
	);

	readonly withdrawRoute = CORE_ROUTE.payment.withdraw;
	readonly depositRoute = CORE_ROUTE.payment.deposit;

	isGameLaunched = false;

	readonly accountInteraction: UiInteraction = {
		...BALANCE_INTERACTION,
		data: { ItemClicked: HeaderAction.UserAccount },
	};
	private data$$: Subscription | undefined;
	private bonusVisible = true;
	private readonly BETA_BADGE_KEY = "beta-site-navigation.leave-beta-site";

	constructor(
		private store: Store<CommonState & CoreContentState>,
		private walletSelector: WalletSelector,
		private betaBadgeService: BetaBadgeService,
		private translationService: TranslationService,
		private changeDetector: ChangeDetectorRef,
		private telemetryService: TelemetryService,
		private userSummaryConfigService: UserSummaryConfigService,
		@Optional() @Inject(MAT_CORE_CONFIG) private matCoreConfig: MatCoreConfig
	) {}

	ngOnInit() {
		const bonusVisible$ = this.userSummaryConfigService.getBonusVisible$().pipe(
			ignoreNil(),
			tap((bonusVisible) => (this.bonusVisible = bonusVisible))
		);

		const isGameLaunched$ = this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			tap((route) => {
				this.isGameLaunched = !!(route?.name && GAMING_GAME_LAUNCHERS.includes(route?.name));
			})
		);

		this.data$$ = merge(bonusVisible$, isGameLaunched$).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	menuItemSelected(menuItemClicked: string) {
		this.menu?.closeMenu();
		if (menuItemClicked === this.BETA_BADGE_KEY) {
			this.betaBadgeService.goBackToOldSite();
			return;
		}
		this.telemetryService.track({
			event: MyAccountEvent.MyAccountClick,
			ItemClicked: menuItemClicked,
		});
	}

	get fundsOverviewOptions(): FundsOverviewOptions {
		return { bonusVisible: this.bonusVisible };
	}
}
